/**
 * Generates guidance for the AI agent based on the topic classification response and AI matches result
 * @param {Object} result - The result from runAIMatches API call
 * @param {Object} response - The response from the topic classification
 * @param {Object} queries - The queries dictionary for looking up query names
 * @returns {string} - The agent guidance message
 */
export const generateAgentGuidance = (result, response, queries, discoveryState) => {
  // Default guidance if we can't determine anything specific
  let guidance = "Please provide a helpful response based on the user's query.";
  
  // Parse the search_gen_response.icp_requirements array if available
  if (result && result.search_gen_response && result.search_gen_response.icp_requirements) {
    const icpRequirements = result.search_gen_response.icp_requirements;
    // Pass the discovery_topic from the response to parseIcpRequirements
    const discoveryTopic = response && response.discovery_topic ? response.discovery_topic : null;
    guidance = parseIcpRequirements(icpRequirements, queries, discoveryState, discoveryTopic);
  }
  // If no icp_requirements, fall back to topic-based guidance
  else if (response && response.current_topic) {
    const topic = response.current_topic;
    const discoveryTopic = response.discovery_topic || null;
    
    // Generate guidance based on the topic
    switch (topic) {
      case "CURRENT_JOB_TITLE_ROLE":
      case "PAST_JOB_TITLE_ROLE":
        guidance = generateTitleGuidance(result, topic);
        break;
      case "LOCATION":
        guidance = generateLocationGuidance(result, topic);
        break;
      case "INDUSTRY":
        guidance = generateIndustryGuidance(result, topic);
        break;
      case "COMPANY_NAME":
      case "COMPANY_STAGE":
      case "COMPANY_BUSINESS_MODEL":
      case "COMPANY_SIZE":
        guidance = generateCompanyGuidance(result, topic);
        break;
      case "SKILLS_MUSTHAVE":
      case "SKILLS_NICETOHAVE":
        guidance = generateSkillsGuidance(result, topic);
        break;
      case "OVERALL_EXPERIENCE":
        guidance = generateExperienceGuidance(result, topic);
        break;
      case "SCHOOL_NAME":
      case "DEGREE":
      case "MAJOR":
        guidance = generateEducationGuidance(result, topic);
        break;
      default:
        guidance = generateDefaultGuidance(result, topic);
    }
    
    // Add discovery context if available
    if (discoveryTopic) {
      guidance += `\n\nThis relates to the "${discoveryTopic}" discovery topic. Ensure your response helps progress the discovery process.`;
    }
  }
  
  // Format the final agent guidance message
  return `Agent guidance: ${guidance}`;
};

/**
 * Parse the icp_requirements array and generate guidance based on the section_name and bool_str fields
 * @param {Array} icpRequirements - The icp_requirements array from the search_gen_response
 * @param {Object} queries - The queries dictionary for looking up query names
 * @param {Object} discoveryState - The current discovery state
 * @param {string} detectedDiscoveryTopic - The discovery topic detected by monitorAgentPromptWithDiscovery
 * @returns {string} - The guidance message
 */
const parseIcpRequirements = (icpRequirements, queries, discoveryState, detectedDiscoveryTopic) => {
  if (!icpRequirements || icpRequirements.length === 0) {
    return "No search requirements found. Please provide more information.";
  }
  
  let guidance = "";
  
  // Process each requirement
  for (const requirement of icpRequirements) {
    const { section_name, bool_str, section_type, section_id } = requirement;
    
    // Skip if no bool_str
    if (!bool_str) continue;
    
    // Parse the bool_str to extract keywords or query IDs
    const parsedBoolStr = parseBoolStr(bool_str, queries);
    
    // Add section-specific guidance
    guidance += `\n\n${section_name} (${section_type}): ${parsedBoolStr}`;
    
    // Use the detected discovery topic if available, otherwise map section_name to discovery topic
    let discoveryTopic = detectedDiscoveryTopic;
    
    // If no detected discovery topic, try to map section_name to a discovery topic
    if (!discoveryTopic) {
      if (section_name === "Must Have Skills") {
        discoveryTopic = "skills";
      } else if (section_name.includes("Location")) {
        discoveryTopic = "location";
      } else if (section_name.includes("Title")) {
        discoveryTopic = "title";
      } else if (section_name.includes("Company") || section_name.includes("Industry")) {
        discoveryTopic = "company";
      } else if (section_name.includes("Experience")) {
        discoveryTopic = "experience";
      } else if (section_name.includes("Education") || section_name.includes("Degree") || section_name.includes("School")) {
        discoveryTopic = "education";
      }
    }
    
    // Add specific recommendations based on section_name
    switch (section_name) {
      case "Must Have Skills":
        guidance += `\nThe user has mentioned these skills. Pls note the and and or (booleans) - if its an OR then its one of those skills. Confirm if these are the essential skills they're looking for and ask if there are any other critical skills to add.`;
        break;
      case "Findem Magic":
        // For Findem Magic, provide more detailed explanations using query descriptions
        guidance += `\nI've added these specialized filters to enhance your search results:`;
        
        // Extract the query IDs from the bool_str
        const queryIds = bool_str.match(/[0-9a-f]{24}/g) || [];
        
        // Add detailed explanations for each query
        queryIds.forEach(id => {
          if (queries && queries[id]) {
            const queryName = queries[id].name || id;
            const queryDescription = queries[id].description || "No description available";
            
            guidance += `\n• ${queryName}: ${queryDescription}`;
          }
        });
        
        guidance += `\nThese filters will help you find candidates with the specific qualifications and experience that match your requirements. They leverage Findem's proprietary data and algorithms to identify candidates who might not be discoverable through traditional keyword searches alone.`;
        break;
      default:
        guidance += `\nAcknowledge these requirements and ask if they accurately reflect what the user is looking for.`;
    }
    
    // Check if this is the current discovery topic
    const isCurrentDiscoveryTopic = discoveryState && 
                                   discoveryState.currentDiscoTopic && 
                                   discoveryTopic === discoveryState.currentDiscoTopic;
    
    if (isCurrentDiscoveryTopic) {
      guidance += `\n\nThis matches the current discovery topic "${discoveryTopic}". After confirming these details with the user, suggest moving to the next topic in the discovery sequence.`;
    } else if (discoveryTopic) {
      guidance += `\nThis relates to the "${discoveryTopic}" discovery topic.`;
    }
  }
  
  return guidance;
};

/**
 * Parse the bool_str to extract keywords or query IDs
 * @param {string} boolStr - The bool_str from the icp_requirement
 * @param {Object} queries - The queries dictionary for looking up query names
 * @returns {string} - The parsed bool_str
 */
const parseBoolStr = (boolStr, queries) => {
  if (!boolStr) return "";
  
  // Remove parentheses and quotes
  let cleanedStr = boolStr.replace(/[()'"]/g, "");
  
  // Split by OR or AND operators
  const terms = cleanedStr.split(/\s+OR\s+|\s+AND\s+/);
  
  // Process each term
  const processedTerms = terms.map(term => {
    term = term.trim();
    
    // Check if the term is a hex ID (query ID)
    if (/^[0-9a-f]{24}$/.test(term) && queries && queries[term]) {
      return queries[term].name || term;
    }
    
    return term;
  });
  
  return processedTerms.join(", ");
};

/**
 * Generate guidance for title-related topics
 */
const generateTitleGuidance = (result, topic) => {
  // Check if we have matches in the result
  if (result && result.matches && result.matches.length > 0) {
    const matchCount = result.matches.length;
    return `The user is discussing job titles. I found ${matchCount} potential matches based on this title. Confirm the title details and ask if they want to refine it further. Suggest alternatives if appropriate.`;
  }
  
  return `The user is discussing job titles. Ask clarifying questions to understand the exact title they're looking for, including level (junior, senior, etc.) and any specific variations they're considering.`;
};

/**
 * Generate guidance for location-related topics
 */
const generateLocationGuidance = (result, topic) => {
  return `The user is discussing location preferences. Clarify if they're open to remote work, hybrid, or specific locations. If specific locations are mentioned, acknowledge them and ask if there are any other locations they're considering.`;
};

/**
 * Generate guidance for industry-related topics
 */
const generateIndustryGuidance = (result, topic) => {
  return `The user is discussing industry preferences. Acknowledge the industries mentioned and ask if there are specific sectors within these industries they're most interested in.`;
};

/**
 * Generate guidance for company-related topics
 */
const generateCompanyGuidance = (result, topic) => {
  let guidance = "The user is discussing company preferences. ";
  
  switch (topic) {
    case "COMPANY_NAME":
      guidance += "They've mentioned specific companies. Acknowledge these and ask if there are similar companies they'd be interested in.";
      break;
    case "COMPANY_STAGE":
      guidance += "They're discussing company stage (startup, established, etc.). Clarify their preferences and ask about size if not mentioned.";
      break;
    case "COMPANY_BUSINESS_MODEL":
      guidance += "They're discussing business models. Clarify their preferences and ask about industries if not mentioned.";
      break;
    case "COMPANY_SIZE":
      guidance += "They're discussing company size. Clarify their preferences and ask about stage if not mentioned.";
      break;
    default:
      guidance += "Ask clarifying questions about company type, size, stage, and industry.";
  }
  
  return guidance;
};

/**
 * Generate guidance for skills-related topics
 */
const generateSkillsGuidance = (result, topic) => {
  const isMustHave = topic === "SKILLS_MUSTHAVE";
  
  return `The user is discussing ${isMustHave ? 'essential' : 'nice-to-have'} skills. Acknowledge the skills mentioned and ask if there are any other ${isMustHave ? 'critical' : 'preferred'} skills they're looking for.`;
};

/**
 * Generate guidance for experience-related topics
 */
const generateExperienceGuidance = (result, topic) => {
  let guidance = `The user is discussing experience requirements. `;
  
  // Check if we have experience histogram data
  if (result && result.experienceHistogram) {
    // Extract the match counts from the histogram
    const matchCounts = [];
    const experienceHistogram = result.experienceHistogram;
    
    // Process the histogram data
    if (experienceHistogram && experienceHistogram.matches) {
      // Iterate through the matches to extract experience ranges and counts
      for (let i = 0; i < 31; i++) {
        const sectionId = `internal_workexp_${i}`;
        const matchCount = experienceHistogram.matches[sectionId] ? experienceHistogram.matches[sectionId].count : 0;
        
        if (matchCount > 0) {
          const startYear = Math.floor(i / 12);
          const endYear = Math.floor((i + 1) / 12);
          
          // Format the experience range
          let rangeLabel;
          if (startYear === 0) {
            if (endYear === 0) {
              rangeLabel = "Less than 1 year";
            } else {
              rangeLabel = "Less than 1 year";
            }
          } else if (startYear === endYear) {
            rangeLabel = `${startYear} year${startYear !== 1 ? 's' : ''}`;
          } else {
            rangeLabel = `${startYear}-${endYear} years`;
          }
          
          matchCounts.push({ range: rangeLabel, count: matchCount });
        }
      }
      
      // Sort by count in descending order
      matchCounts.sort((a, b) => b.count - a.count);
      
      // Add the histogram data to the guidance
      if (matchCounts.length > 0) {
        guidance += `Based on the candidate pool, here's the distribution of experience levels:\n`;
        
        // Add the top 5 experience ranges
        const topRanges = matchCounts.slice(0, 5);
        topRanges.forEach(({ range, count }) => {
          guidance += `\n• ${range}: ${count} candidates`;
        });
        
        guidance += `\n\nThis data can help guide the conversation about experience requirements. `;
      }
    }
  }
  
  guidance += `Clarify the years of experience needed and any specific experience types (e.g., industry experience, leadership experience). Ask if they have a specific range in mind or if they're flexible based on the candidate pool.`;
  
  return guidance;
};

/**
 * Generate guidance for education-related topics
 */
const generateEducationGuidance = (result, topic) => {
  let guidance = "The user is discussing education requirements. ";
  
  switch (topic) {
    case "SCHOOL_NAME":
      guidance += "They've mentioned specific schools. Clarify if these are preferences or requirements.";
      break;
    case "DEGREE":
      guidance += "They're discussing degree requirements. Clarify the level (Bachelor's, Master's, etc.) and if alternatives are acceptable.";
      break;
    case "MAJOR":
      guidance += "They're discussing specific majors. Clarify if these are strict requirements or if related fields are acceptable.";
      break;
    default:
      guidance += "Ask clarifying questions about education level, fields of study, and any certification requirements.";
  }
  
  return guidance;
};

/**
 * Generate default guidance for other topics
 */
const generateDefaultGuidance = (result, topic) => {
  return `The user's message has been classified as "${topic}". Provide a helpful response that addresses their query and asks relevant follow-up questions to gather more information.`;
};

export default generateAgentGuidance;
